import { AlertCircle, ChevronDown, Loader2, Search, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  centsToDisplay,
  usePromotionalAdDestinationCategories,
  usePromotionalAdDestinationProducts,
  useTranslation,
  type AdminStorePromotionalAdDestination,
  type PromotionalAdDestinationCategoryOption,
  type PromotionalAdDestinationProductOption,
  type TranslationKey,
} from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

export interface PromotionalAdProductPickerProps {
  readonly storeId: string | null;
  readonly selectedProductId: string | null;
  readonly initialDestination?: AdminStorePromotionalAdDestination | null;
  readonly disabled?: boolean;
  readonly errorKey?: TranslationKey | null;
  readonly onSelect: (productId: string) => void;
  readonly onClear: () => void;
}

function getInitialProduct(
  destination: AdminStorePromotionalAdDestination | null | undefined,
): PromotionalAdDestinationProductOption | null {
  if (destination?.type !== 'product') return null;
  return {
    id: destination.productId,
    name: destination.productName ?? destination.productId,
    categoryId: null,
    categoryName: destination.categoryName,
    price_cents: 0,
    sale_price_cents: null,
    currency: 'ILS',
    published: destination.isPublished ?? false,
    available: destination.available ?? 0,
    isSelectable: destination.isAvailable,
  };
}

function formatPrice(cents: number, lang: string): string {
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'ILS',
    maximumFractionDigits: 2,
  }).format(centsToDisplay(cents));
}

export function PromotionalAdProductPicker({
  storeId,
  selectedProductId,
  initialDestination,
  disabled = false,
  errorKey,
  onSelect,
  onClear,
}: PromotionalAdProductPickerProps) {
  const { t, dir, lang } = useTranslation();
  const [search, setSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search, 250);
  const debouncedCategorySearch = useDebouncedValue(categorySearch, 250);
  const categoryQuery = usePromotionalAdDestinationCategories(supabase, {
    storeId,
    search: debouncedCategorySearch,
  });
  const query = usePromotionalAdDestinationProducts(supabase, {
    storeId,
    search: debouncedSearch,
    categoryId,
  });
  const initialProduct = getInitialProduct(initialDestination);
  const [selectedProductCache, setSelectedProductCache] =
    useState<PromotionalAdDestinationProductOption | null>(initialProduct);
  const [selectedCategoryCache, setSelectedCategoryCache] =
    useState<PromotionalAdDestinationCategoryOption | null>(null);

  const products = useMemo(() => {
    const rows = query.data ?? [];

    if (!selectedProductId || rows.some((row) => row.id === selectedProductId)) {
      return rows;
    }

    if (initialProduct?.id === selectedProductId) {
      return [initialProduct, ...rows];
    }

    if (selectedProductCache?.id === selectedProductId) {
      return [selectedProductCache, ...rows];
    }

    return rows;
  }, [initialProduct, query.data, selectedProductCache, selectedProductId]);

  const selected = products.find((product) => product.id === selectedProductId) ?? null;
  const categories = categoryQuery.data ?? [];
  const selectedCategory =
    categories.find((category) => category.id === categoryId) ??
    (selectedCategoryCache?.id === categoryId ? selectedCategoryCache : null);

  useEffect(() => {
    if (selected) setSelectedProductCache(selected);
  }, [selected]);

  useEffect(() => {
    if (selectedCategory) setSelectedCategoryCache(selectedCategory);
  }, [selectedCategory]);

  return (
    <div dir={dir} className="space-y-3" data-testid="promotional-ad-product-picker">
      <div className="space-y-2">
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-expanded={isCategoryFilterOpen}
          aria-controls="promotional-ad-product-category-filter-panel"
          className="min-h-11 w-full justify-between text-start"
          data-testid="promotional-ad-product-category-filter-toggle"
          onClick={() => setIsCategoryFilterOpen((current) => !current)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <SlidersHorizontal className="size-4 shrink-0" aria-hidden />
            <span className="min-w-0 truncate">
              {t('admin.promotionalAds.destination.productCategoryFilterButton' as TranslationKey, {
                name:
                  selectedCategory?.name ??
                  t('admin.promotionalAds.destination.productCategoryFilterAll' as TranslationKey),
              })}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'text-muted-foreground size-4 shrink-0 transition-transform',
              isCategoryFilterOpen && 'rotate-180',
            )}
            aria-hidden
          />
        </Button>

        {isCategoryFilterOpen ? (
          <div
            id="promotional-ad-product-category-filter-panel"
            className="border-border bg-card space-y-2 rounded-lg border p-3"
          >
            <div>
              <Label htmlFor="promotional-ad-product-category-search" className="sr-only">
                {t(
                  'admin.promotionalAds.destination.productCategoryFilterSearchLabel' as TranslationKey,
                )}
              </Label>
              <div className="relative">
                <Search
                  className="text-muted-foreground pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2"
                  aria-hidden
                />
                <Input
                  id="promotional-ad-product-category-search"
                  value={categorySearch}
                  onChange={(event) => setCategorySearch(event.currentTarget.value)}
                  className="ps-9"
                  disabled={disabled}
                  placeholder={t(
                    'admin.promotionalAds.destination.productCategoryFilterSearchPlaceholder' as TranslationKey,
                  )}
                  data-testid="promotional-ad-product-category-search"
                />
              </div>
            </div>

            <div className="max-h-52 overflow-y-auto" role="listbox">
              <Button
                type="button"
                variant={categoryId ? 'ghost' : 'default'}
                size="sm"
                disabled={disabled}
                className="mb-1 w-full justify-start"
                role="option"
                aria-selected={!categoryId}
                onClick={() => {
                  setCategoryId(null);
                  setIsCategoryFilterOpen(false);
                }}
              >
                {t('admin.promotionalAds.destination.productCategoryFilterAll' as TranslationKey)}
              </Button>

              {categories.map((category) => (
                <Button
                  key={category.id}
                  type="button"
                  variant={categoryId === category.id ? 'default' : 'ghost'}
                  size="sm"
                  disabled={disabled || !category.isSelectable}
                  className="w-full justify-between gap-3"
                  role="option"
                  aria-selected={categoryId === category.id}
                  onClick={() => {
                    setCategoryId(category.id);
                    setIsCategoryFilterOpen(false);
                  }}
                  data-testid={`promotional-ad-product-category-filter-${category.id}`}
                >
                  <span className="min-w-0 truncate">{category.name}</span>
                  <span className="text-xs opacity-70">{category.productCount}</span>
                </Button>
              ))}

              {!categoryQuery.isLoading && !categoryQuery.isError && categories.length === 0 ? (
                <p className="text-muted-foreground px-2 py-3 text-start text-sm">
                  {t('admin.promotionalAds.destination.categoryEmpty' as TranslationKey)}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div>
        <Label htmlFor="promotional-ad-product-search" className="mb-1.5 block text-start">
          {t('admin.promotionalAds.destination.productSearchLabel' as TranslationKey)}
        </Label>
        <div className="relative">
          <Search
            className="text-muted-foreground pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            id="promotional-ad-product-search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            className="ps-9"
            disabled={disabled}
            placeholder={t(
              'admin.promotionalAds.destination.productSearchPlaceholder' as TranslationKey,
            )}
            data-testid="promotional-ad-product-search"
          />
        </div>
      </div>

      {selected ? (
        <div className="border-border bg-admin-surface-muted flex items-start justify-between gap-3 rounded-lg border p-3 text-start">
          <div className="min-w-0">
            <p className="text-foreground text-sm font-medium">
              {t('admin.promotionalAds.destination.selectedProduct' as TranslationKey, {
                name: selected.name,
              })}
            </p>
            {!selected.isSelectable ? (
              <p className="text-muted-foreground mt-1 text-xs">
                {t('admin.promotionalAds.destination.productUnavailable' as TranslationKey)}
              </p>
            ) : null}
          </div>
          <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={onClear}>
            <X className="size-4" aria-hidden />
            <span>{t('admin.promotionalAds.destination.clearSelection' as TranslationKey)}</span>
          </Button>
        </div>
      ) : null}

      {errorKey ? (
        <p role="alert" className="text-destructive text-start text-xs">
          {t(errorKey)}
        </p>
      ) : null}

      {query.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-start text-sm">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          <span>{t('admin.promotionalAds.pickers.loadingProducts' as TranslationKey)}</span>
        </div>
      ) : null}

      {query.isError ? (
        <div
          role="alert"
          className="border-destructive/25 bg-destructive/10 text-destructive flex items-start justify-between gap-3 rounded-lg border p-3 text-start text-sm"
        >
          <span className="flex min-w-0 items-start gap-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t('common.errors.unknown' as TranslationKey)}
          </span>
          <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('admin.promotionalAds.pickers.retryProducts' as TranslationKey)}
          </Button>
        </div>
      ) : null}

      {!query.isLoading && !query.isError && products.length === 0 ? (
        <p className="text-muted-foreground rounded-lg border border-dashed p-4 text-start text-sm">
          {t('admin.promotionalAds.destination.productEmpty' as TranslationKey)}
        </p>
      ) : null}

      {products.length > 0 ? (
        <div className="border-border divide-border max-h-80 overflow-y-auto rounded-lg border">
          {products.map((product) => {
            const isSelected = product.id === selectedProductId;
            const priceCents = product.sale_price_cents ?? product.price_cents;
            const isUnavailable = !product.isSelectable;
            return (
              <button
                key={product.id}
                type="button"
                disabled={disabled || isUnavailable}
                onClick={() => onSelect(product.id)}
                className={cn(
                  'hover:bg-admin-surface-muted flex w-full flex-col gap-2 px-4 py-3 text-start text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected && 'bg-admin-surface-muted',
                )}
                data-testid={`promotional-ad-product-option-${product.id}`}
              >
                <span className="flex w-full min-w-0 items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="text-foreground block truncate font-medium">
                      {product.name}
                    </span>
                    {product.categoryName ? (
                      <span className="text-muted-foreground block truncate text-xs">
                        {product.categoryName}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-foreground shrink-0 text-xs font-medium">
                    {formatPrice(priceCents, lang)}
                  </span>
                </span>
                <span className="flex flex-wrap gap-1.5">
                  <Badge variant={product.published ? 'success' : 'neutral'}>
                    {t(
                      product.published
                        ? ('admin.promotionalAds.pickers.productStatus.published' as TranslationKey)
                        : ('admin.promotionalAds.pickers.productStatus.draft' as TranslationKey),
                    )}
                  </Badge>
                  <Badge variant={product.available > 0 ? 'success' : 'neutral'}>
                    {t(
                      product.available > 0
                        ? ('admin.promotionalAds.pickers.availability.inStock' as TranslationKey)
                        : ('admin.promotionalAds.pickers.availability.outOfStock' as TranslationKey),
                    )}
                  </Badge>
                  {isUnavailable ? (
                    <Badge variant="outline">
                      {t('admin.promotionalAds.destination.productUnavailable' as TranslationKey)}
                    </Badge>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
